import React, { useEffect, useState } from 'react';
import { webhooksApi } from '@/utils/api';
import { Plus, Trash2, X, Bell, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function NotificationsPanel() {
  const [channels, setChannels] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: 'discord', name: '', url: '', events: ['sla_breach'], enabled: true });
  const EVENTS = [
    { key: 'sla_breach', label: 'Bug SLA breached' },
    { key: 'new_bug', label: 'New bug reported' },
  ];

  const reload = () => webhooksApi.list().then(r => setChannels(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const handleAdd = async () => {
    try {
      await webhooksApi.create(form);
      setShowAdd(false);
      setForm({ type: 'discord', name: '', url: '', events: ['sla_breach'], enabled: true });
      reload();
      toast.success('Channel added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete webhook channel?')) return;
    await webhooksApi.delete(id);
    reload();
  };

  const handleTest = async (id) => {
    try {
      const r = await webhooksApi.test(id);
      if (r.data.sent) toast.success('Test message sent');
      else toast.error('Webhook failed — check URL');
    } catch { toast.error('Test failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold" style={{ color: 'var(--ink)' }}>Notification Channels</h2>
        <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={() => setShowAdd(true)} data-testid="add-webhook-button">
          <Plus size={13} /> Add Channel
        </button>
      </div>

      {channels.length === 0 ? (
        <div className="card-base p-8 text-center">
          <Bell size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No channels configured</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Add Discord/Slack webhooks to get notified on SLA breaches and new bugs.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {channels.map(c => (
            <div key={c.id} className="card-base p-3 flex items-center justify-between" data-testid={`webhook-${c.id}`}>
              <div>
                <p className="font-semibold text-sm capitalize" style={{ color: 'var(--ink)' }}>{c.name || c.type}</p>
                <p className="text-xs font-mono truncate" style={{ color: 'var(--muted)', maxWidth: 400 }}>{c.url}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>Events: {c.events?.join(', ')}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleTest(c.id)} className="text-xs px-2.5 py-1 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#E6F1F4', color: '#006080' }} data-testid={`test-webhook-${c.id}`}>
                  <Send size={11} /> Test
                </button>
                <button onClick={() => handleDelete(c.id)} className="p-1" style={{ color: '#B34A3A' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-md p-5 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>Add webhook</h3>
              <button onClick={() => setShowAdd(false)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Type</label>
                  <select className="input-agency text-sm" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                    <option value="discord">Discord</option>
                    <option value="slack">Slack</option>
                    <option value="webhook">Generic Webhook</option>
                  </select>
                </div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Name</label>
                  <input className="input-agency text-sm" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ops channel" />
                </div>
              </div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Webhook URL *</label>
                <input className="input-agency text-sm font-mono" value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://discord.com/api/webhooks/..." data-testid="webhook-url-input" />
              </div>
              <div>
                <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Events</p>
                <div className="space-y-1.5">
                  {EVENTS.map(e => (
                    <label key={e.key} className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                      <input type="checkbox" checked={form.events.includes(e.key)} onChange={x => {
                        setForm(f => ({ ...f, events: x.target.checked ? [...f.events, e.key] : f.events.filter(y => y !== e.key) }));
                      }} />
                      {e.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3"><button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowAdd(false)}>Cancel</button><button type="button" className="flex-1 btn-primary py-2 text-sm" onClick={handleAdd} disabled={!form.url} data-testid="save-webhook-submit">Add</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
